import {
	Box,
	Typography,
	Alert,
	AlertTitle,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Chip,
	Button,
} from "@mui/material";
import { WarningAmber, InsertDriveFileOutlined, NotInterested, Upload } from "@mui/icons-material";
import type { FileValidationProps } from "./FileValidation.types";
import { useFileValidationViewModel } from "./FileValidation.vm";
import "./FileValidation.css";

/**
 * FileValidation step component
 *
 * Displays file parsing warnings and list of uploaded files.
 */
export default function FileValidation(props: FileValidationProps) {
	const vm = useFileValidationViewModel(props);

	return (
		<Box className="file-validation" sx={{ p: 2 }}>
			{/* Header with Add Files / Clear buttons */}
			<Box sx={{ mb: 3 }}>
				<Typography variant="body2" color="text.secondary">
					Click &apos;Add Files&apos; to upload shapefiles, GeoJSON, KML, or ZIP files containing geodata
				</Typography>
				<Button
					variant="contained"
					onClick={props.onAddFiles}
					startIcon={<Upload />}
					sx={{ flex: "1 1 80%", mt: 2, mr: 1 }}
				>
					Add Files
				</Button>
				<Button
					variant="contained"
					onClick={props.onClear}
					startIcon={<NotInterested />}
					color="secondary"
					sx={{ flex: "1 1 20%", mt: 2 }}
					disabled={!vm.hasFiles}
				>
					Clear
				</Button>
			</Box>

			{/* Empty State - No Files */}
			{!vm.hasFiles && (
				<Box sx={{ textAlign: "center", py: 4 }}>
					<InsertDriveFileOutlined sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
					<Typography variant="body1" color="text.secondary">
						No files uploaded yet
					</Typography>
					<Typography variant="body2" color="text.disabled">
						Supported formats: .shp, .geojson, .json, .kml, .kmz, .zip
					</Typography>
				</Box>
			)}

			{/* Uploaded Files Section */}
			{vm.hasFiles && (
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
						<InsertDriveFileOutlined fontSize="small" />
						Uploaded Files ({vm.totalFiles})
					</Typography>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						{props.uploadedFiles.map((file, index) => (
							<Chip
								key={`file-${index}`}
								label={file.name}
								size="small"
								icon={<InsertDriveFileOutlined />}
								variant="outlined"
							/>
						))}
					</Box>
				</Box>
			)}

			{/* Warnings Section */}
			{vm.hasFiles && vm.hasWarnings && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle2" sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
						<WarningAmber color="warning" fontSize="small" />
						Warnings ({vm.totalWarnings})
					</Typography>

					{/* Unsupported Extension Warnings */}
					{vm.warningsByType.unsupported_extension.length > 0 && (
						<Alert severity="warning" sx={{ mb: 1 }}>
							<AlertTitle>Unsupported File Types</AlertTitle>
							<Typography variant="body2" sx={{ mb: 1 }}>
								The following files are unsupported, and will not be processed. Supported formats are: .shp, .geojson,
								.json, .kml, .kmz, .zip
							</Typography>
							<List dense>
								{vm.warningsByType.unsupported_extension.map((warning, index) => (
									<ListItem key={`unsupported-${index}`} disablePadding>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<InsertDriveFileOutlined fontSize="small" />
										</ListItemIcon>
										<ListItemText
											primary={warning.fileName}
											secondary={warning.message}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
							</List>
						</Alert>
					)}

					{/* Parse Failed Warnings */}
					{vm.warningsByType.parse_failed.length > 0 && (
						<Alert severity="warning" sx={{ mb: 1 }}>
							<AlertTitle>Parse Errors</AlertTitle>
							<Typography variant="body2" sx={{ mb: 1 }}>
								We were unable to parse the following files:
							</Typography>
							<List dense>
								{vm.warningsByType.parse_failed.map((warning, index) => (
									<ListItem key={`parse-${index}`} disablePadding>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<InsertDriveFileOutlined fontSize="small" />
										</ListItemIcon>
										<ListItemText
											primary={warning.fileName}
											secondary={warning.message}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
							</List>
						</Alert>
					)}

					{/* Empty File Warnings */}
					{vm.warningsByType.empty_file.length > 0 && (
						<Alert severity="warning" sx={{ mb: 1 }}>
							<AlertTitle>Empty Files</AlertTitle>
							<Typography variant="body2" sx={{ mb: 1 }}>
								The following files contain no data:
							</Typography>
							<List dense>
								{vm.warningsByType.empty_file.map((warning, index) => (
									<ListItem key={`empty-${index}`} disablePadding>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<InsertDriveFileOutlined fontSize="small" />
										</ListItemIcon>
										<ListItemText
											primary={warning.fileName}
											secondary={warning.message}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
							</List>
						</Alert>
					)}

					{/* Invalid Geometry Warnings */}
					{vm.warningsByType.invalid_geometry.length > 0 && (
						<Alert severity="warning" sx={{ mb: 1 }}>
							<AlertTitle>Invalid Geometry</AlertTitle>
							<Typography variant="body2" sx={{ mb: 1 }}>
								The following files contain invalid geo-data:
							</Typography>
							<List dense>
								{vm.warningsByType.invalid_geometry.map((warning, index) => (
									<ListItem key={`invalid-${index}`} disablePadding>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<InsertDriveFileOutlined fontSize="small" />
										</ListItemIcon>
										<ListItemText
											primary={warning.fileName}
											secondary={warning.message}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
							</List>
						</Alert>
					)}
				</Box>
			)}
		</Box>
	);
}
